import { get, post, put, del } from "@/shared/utils/fetchApi";
import { API_CONFIG } from "@/infrastructure/config/api";
import { ChatBase, CreateChatPayload, RenameChatPayload, ChatHistoryParams, SendMessageWithChatParams, ChatHistoryMessage } from "../types/chatBase";

/**
 * Envelope da listagem v2 (`GET /chats`). DTO flat já em snake_case = ChatBase.
 */
interface ChatListResponse {
  data: ChatBase[];
  pagination: {
    total_items: number;
    limit: number;
    offset: number;
    total_pages: number;
  };
}

/**
 * MessageDto flat retornado pelo `POST /chats/:id/send` (api-v2). `message` é o jsonb
 * `{type, content, sources}`. Normalizado em sendMessageWithChatId pro shape do caller.
 */
interface MessageDto {
  id: string;
  chat_base_id: string | null;
  session_id: string;
  message: { type?: string; content?: string; sources?: unknown[] };
  sender: string;
  metadata: Record<string, unknown> | null;
  product_id: string | null;
  organization_id: string | null;
  user_id: string | null;
  created_at: string | null;
}

/**
 * Envelope do histórico v2 (`GET /chats/:id/messages`). `data` são MessageDto
 * aninhados; normalizados pro shape flat em getChatHistoryWithChatId.
 */
interface ChatHistoryResponse {
  data: MessageDto[];
  pagination: {
    total_items: number;
    limit: number;
    offset: number;
    total_pages: number;
  };
}

/**
 * Dados do histórico de chat
 */
interface ChatHistoryData {
  messages: ChatHistoryMessage[];
  stats: {
    message_count: number;
    last_activity: string;
    unique_users: number;
  };
}

/**
 * Serviço para integração com os endpoints de múltiplos chats
 * Base URL: /chats
 */
export class ChatBaseService {

  /**
   * 1. Listar Chats do Produto
   * GET /chats/{workspaceId}
   * @param workspaceId ID do produto
   * @returns Lista de chats do usuário para o produto
   */
  static async getChats(
    workspaceId: string,
  ): Promise<{ data: ChatBase[]; error: string | null }> {
    try {
      // product_id é query (workspaceId === productId). DTO flat = ChatBase.
      // limit=100 (cap do v2) cobre o sidebar; legacy era unbounded.
      const res = await get<ChatListResponse>(
        `/chats?product_id=${workspaceId}&limit=100`,
      );
      return { data: res.data, error: null };
    } catch (error: unknown) {
      console.error("[ChatBaseService] Error fetching chats:", error);
      return {
        data: [],
        error: error instanceof Error ? error.message : "Erro de conexão",
      };
    }
  }

  /**
   * 2. Criar Novo Chat
   * POST /chats/{workspaceId}
   * @param workspaceId ID do produto
   * @param payload Dados para criar o chat (title opcional)
   * @returns Chat criado
   */
  static async createChat(
    workspaceId: string,
    payload: CreateChatPayload = {},
  ): Promise<{ data: ChatBase | null; error: string | null }> {
    try {
      // product_id vai no body; title opcional. Resposta flat = ChatBase.
      const res = await post<ChatBase>(`/chats`, {
        product_id: workspaceId,
        ...(payload.title ? { title: payload.title } : {}),
      });
      return { data: res, error: null };
    } catch (error: unknown) {
      console.error("[ChatBaseService] Error creating chat:", error);
      return {
        data: null,
        error: error instanceof Error ? error.message : "Erro de conexão",
      };
    }
  }

  /**
   * 3. Obter Chat Específico
   * GET /chat/{chatId}
   * @param chatId ID do chat
   * @returns Chat específico
   */
  static async getChat(
    chatId: string,
  ): Promise<{ data: ChatBase | null; error: string | null }> {
    try {
      const res = await get<ChatBase>(`/chats/${chatId}`);
      return { data: res, error: null };
    } catch (error: unknown) {
      console.error("[ChatBaseService] Error fetching chat:", error);
      return {
        data: null,
        error: error instanceof Error ? error.message : "Erro de conexão",
      };
    }
  }

  /**
   * 4. Renomear Chat
   * PUT /chat/{chatId}/title
   * @param chatId ID do chat
   * @param payload Novo título (1-255 caracteres)
   * @returns Sucesso ou erro
   */
  static async renameChat(
    chatId: string,
    payload: RenameChatPayload,
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      // Validar título
      if (!payload.title || payload.title.trim().length === 0) {
        return { success: false, error: "Título não pode estar vazio" };
      }

      if (payload.title.length > 255) {
        return {
          success: false,
          error: "Título não pode ter mais de 255 caracteres",
        };
      }

      // PUT /chats/:id atualiza o chat inteiro (sem sufixo /title). Throw → catch.
      await put<ChatBase>(`/chats/${chatId}`, { title: payload.title });
      return { success: true, error: null };
    } catch (error: unknown) {
      console.error("[ChatBaseService] Error renaming chat:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Erro de conexão",
      };
    }
  }

  /**
   * 5. Arquivar Chat
   * PUT /chat/{chatId}/archive
   * @param chatId ID do chat
   * @returns Sucesso ou erro
   */
  static async archiveChat(
    chatId: string,
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      await put<ChatBase>(`/chats/${chatId}/archive`);
      return { success: true, error: null };
    } catch (error: unknown) {
      console.error("[ChatBaseService] Error archiving chat:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Erro de conexão",
      };
    }
  }

  /**
   * 6. Excluir Chat
   * DELETE /chat/{chatId}
   * @param chatId ID do chat
   * @returns Sucesso ou erro
   */
  static async deleteChat(
    chatId: string,
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      // v2 soft delete → 200 { id, deleted: true }. Throw → catch trata erro.
      await del<{ id: string; deleted: boolean }>(`/chats/${chatId}`);
      return { success: true, error: null };
    } catch (error: unknown) {
      console.error("[ChatBaseService] Error deleting chat:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Erro de conexão",
      };
    }
  }
}

/**
 * Extensões do PlaygroundService existente para suporte a múltiplos chats
 */
export class PlaygroundMultiChatService {
  /**
   * Obter histórico de chat com chatId
   * GET /chats/:chatId/messages
   * @param params Parâmetros incluindo chatId obrigatório
   * @returns Histórico de mensagens do chat específico
   */
  static async getChatHistoryWithChatId(
    params: ChatHistoryParams,
  ): Promise<{ data: ChatHistoryData; error: string | null }> {
    const emptyData: ChatHistoryData = {
      messages: [],
      stats: {
        message_count: 0,
        last_activity: new Date().toISOString(),
        unique_users: 0,
      },
    };
    try {
      // GET /chats/:id/messages?limit&offset — :id é o chatId, sem
      // workspaceId no path nem prefixo /api. Resposta { data: MessageDto[], pagination }.
      // Normaliza pro shape flat que o PlaygroundView espera (message_type/content/
      // sources no topo). v2 NÃO persiste thinking-trace (decisão D1, SSE-only), então
      // o histórico só traz rows user + assistant — sem replay do trace.
      const v2Query = new URLSearchParams({
        ...(params.limit ? { limit: params.limit.toString() } : {}),
        ...(params.offset ? { offset: params.offset.toString() } : {}),
      });
      const suffix = v2Query.toString() ? `?${v2Query.toString()}` : "";
      const res = await get<ChatHistoryResponse>(
        `/chats/${params.chatId}/messages${suffix}`,
      );
      const messages: ChatHistoryMessage[] = (res.data ?? []).map((m) => {
        const msg = m.message ?? {};
        return {
          id: m.id,
          session_id: m.session_id,
          product_id: m.product_id,
          organization_id: m.organization_id,
          user_id: m.user_id,
          sender: m.sender,
          message_type: msg.type ?? "user",
          content: msg.content ?? "",
          sources: msg.sources ?? [],
          metadata: m.metadata ?? {},
          created_at: m.created_at ?? new Date().toISOString(),
        };
      });
      const lastActivity = messages.length
        ? messages[messages.length - 1].created_at
        : new Date().toISOString();
      const uniqueUsers = new Set(
        messages.map((m) => m.user_id).filter(Boolean),
      ).size;
      return {
        data: {
          messages,
          stats: {
            message_count: res.pagination?.total_items ?? messages.length,
            last_activity: lastActivity,
            unique_users: uniqueUsers,
          },
        },
        error: null,
      };
    } catch (error: unknown) {
      console.error(
        "[PlaygroundMultiChatService] Error fetching chat history:",
        error,
      );
      return {
        data: emptyData,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Enviar mensagem com chatId
   * POST /chats/:chatId/send
   * @param params Parâmetros incluindo mensagem e chatId obrigatório
   * @returns Resposta da mensagem processada
   */
  static async sendMessageWithChatId(
    params: SendMessageWithChatParams,
  ): Promise<{ data: Record<string, unknown> | null; error: string | null }> {
    try {
      // POST /chats/:id/send — `message` é objeto jsonb; chatId no path
      // (não no body); sem prefixo /api. Resposta é MessageDto flat → normaliza pro
      // shape `{user_message}` que o caller (PlaygroundView) já espera.
      const dto = await post<MessageDto>(`/chats/${params.chatId}/send`, {
        message: { content: params.message },
        ...(params.consolidationMode
          ? { consolidationMode: params.consolidationMode }
          : {}),
        ...(params.paginatedItems
          ? { paginatedItems: params.paginatedItems }
          : {}),
        ...(params.originalQuery
          ? { originalQuery: params.originalQuery }
          : {}),
      });
      return {
        data: {
          user_message: {
            id: dto.id,
            chat_base_id: dto.chat_base_id,
            session_id: dto.session_id,
            content: dto.message?.content ?? "",
            message_type: "user",
            user_id: dto.user_id,
            product_id: dto.product_id,
            metadata: dto.metadata,
            created_at: dto.created_at,
          },
        },
        error: null,
      };
    } catch (error: unknown) {
      console.error(
        "[PlaygroundMultiChatService] Error sending message:",
        error,
      );
      return {
        data: null,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Obter URL do SSE com chatId
   * GET /chats/:chatId/stream
   * @param chatId ID do chat
   * @returns URL para conexão Server-Sent Events
   */
  static getSSEUrlWithChatId(chatId: string): string {
    // /chats/:id/stream — sem /api, sem workspaceId, sem ?chatId=.
    // EventSource não envia headers → token via ?access_token= (silentAuth lê query).
    const accessToken = localStorage.getItem("access_token") ?? "";
    return `${API_CONFIG.API_ROOT_URL}/chats/${chatId}/stream?access_token=${encodeURIComponent(accessToken)}`;
  }
}
